// External Flow & Drag in Fluid Dynamics
// Matching style of the Magnetism reference page

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Laminar vs Turbulent Flow ─────────────────────────
function DiagramFlowRegimes() {
  const W = 900, H = 320;
  const PW = (W - 30) / 2;

  // Laminar streamlines — smooth parallel curves
  const laminarLines = [60, 100, 140, 180, 220, 260].map(y => ({
    y, d: `M 20,${y} C 120,${y} 200,${y} 420,${y}`
  }));

  // Turbulent lines — wavy, chaotic
  const turbLines = [
    `M 480,80  C 540,65  570,95  630,75  C 690,55  720,90  860,70`,
    `M 480,110 C 545,130 570,100 625,120 C 680,140 720,105 860,115`,
    `M 480,140 C 530,125 565,160 620,140 C 675,120 715,155 860,140`,
    `M 480,170 C 540,190 568,150 628,175 C 688,200 718,165 860,170`,
    `M 480,200 C 535,185 570,215 625,195 C 680,175 720,210 860,200`,
    `M 480,230 C 545,250 570,215 630,240 C 690,265 720,230 860,240`,
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Flow Regimes — Laminar vs Turbulent</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrRed" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b" opacity="0.8"/>
          </marker>
          <marker id="arrRed" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b" opacity="0.8"/>
          </marker>
        </defs>

        {/* Left panel: Laminar */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={34} textAnchor="middle" fontSize="15" fill="#7B0D0D" fontWeight="700">Laminar Flow (Re &lt; 2300)</text>

        {/* Pipe walls */}
        <rect x={20} y={55} width={400} height={8} rx={2} fill="#888"/>
        <rect x={20} y={270} width={400} height={8} rx={2} fill="#888"/>
        <text x={22} y={50} fontSize="11" fill="#666">Wall</text>
        <text x={22} y={295} fontSize="11" fill="#666">Wall</text>

        {laminarLines.map((l, i) => (
          <path key={i} d={l.d} fill="none" stroke="#c0392b"
            strokeWidth={i===3 ? "2.5" : "1.5"}
            opacity={0.5 + (i===3?0.4:0)}
            markerEnd="url(#arrRed)"
          />
        ))}

        {/* Velocity profile — parabolic */}
        <path d="M 420,63 C 460,80 480,120 480,165 C 480,210 460,250 420,270"
          fill="none" stroke="#27ae60" strokeWidth="2.5" strokeDasharray="5,3"/>
        <text x={484} y={170} fontSize="12" fill="#27ae60" fontWeight="700">Parabolic</text>
        <text x={484} y={185} fontSize="12" fill="#27ae60">profile</text>
        <text x={10+PW/2} y={50} textAnchor="middle" fontSize="12" fill="#c0392b">Smooth, orderly layers — fluid particles move in straight paths</text>

        {/* Info box bottom left */}
        <rect x={16} y={245} width={PW-12} height={42} rx={3} fill="#fff5f5" stroke="#c0392b" strokeWidth="1"/>
        <text x={22} y={261} fontSize="12" fill="#7B0D0D" fontWeight="700">Re = ρvD/μ  (Reynolds number)</text>
        <text x={22} y={278} fontSize="11" fill="#444">Low Re → viscous forces dominate → laminar</text>

        {/* Right panel: Turbulent */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={34} textAnchor="middle" fontSize="15" fill="#7B0D0D" fontWeight="700">Turbulent Flow (Re &gt; 4000)</text>

        <rect x={PW+30} y={55} width={370} height={8} rx={2} fill="#888"/>
        <rect x={PW+30} y={270} width={370} height={8} rx={2} fill="#888"/>

        {turbLines.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="#c0392b"
            strokeWidth="1.5" opacity="0.65" markerEnd="url(#arrRed)"/>
        ))}

        {/* Eddies */}
        {[[580,150,22],[650,120,16],[710,175,20],[760,140,14]].map(([cx,cy,r],i)=>(
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke="#e07b00" strokeWidth="1.2" opacity="0.5" strokeDasharray="3,2"/>
        ))}

        <rect x={PW+26} y={245} width={PW-12} height={42} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={PW+32} y={261} fontSize="12" fill="#7B0000" fontWeight="700">Re &gt; 4000 → turbulent; 2300–4000 → transitional</text>
        <text x={PW+32} y={278} fontSize="11" fill="#444">Inertial forces dominate → chaotic eddies, mixing</text>
      </svg>
      <div style={S.dCaption}>The Reynolds number (Re = ρvD/μ) predicts the flow regime. Laminar flow has smooth parallel layers with a parabolic velocity profile — fastest at the centre, zero at the wall (no-slip). Turbulent flow is chaotic, with eddies and lateral mixing; it has a flatter velocity profile and much higher momentum transfer near walls.</div>
    </div>
  );
}

// ── Diagram 2: Boundary Layer on a Flat Plate ─────────────────────
function DiagramBoundaryLayer() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Boundary Layer Development on a Flat Plate</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60" opacity="0.9"/>
          </marker>
          <marker id="arrRed2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b" opacity="0.9"/>
          </marker>
        </defs>

        {/* Free-stream arrows */}
        {[40,70,100,130,160,190,220].map(y => (
          <line key={y} x1={10} y1={y} x2={60} y2={y} stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#arrRed2)" opacity="0.5"/>
        ))}
        <text x={10} y={30} fontSize="13" fill="#c0392b" fontWeight="700">U∞ (free stream)</text>

        {/* Plate */}
        <rect x={80} y={235} width={800} height={12} rx={2} fill="#555"/>
        <text x={82} y={258} fontSize="12" fill="#555" fontWeight="600">Flat plate (leading edge →)</text>

        {/* Boundary layer thickness curve */}
        <path d="M 80,235 C 200,200 320,175 450,160 C 570,148 650,143 880,138"
          fill="none" stroke="#c0392b" strokeWidth="2.5" strokeDasharray="6,3"/>
        <text x={720} y={128} fontSize="13" fill="#c0392b" fontWeight="700">δ(x) — boundary layer edge</text>

        {/* Laminar region fill */}
        <path d="M 80,235 C 150,220 250,210 360,203 L 360,235 Z"
          fill="#f5c6c6" opacity="0.4"/>
        <text x={180} y={225} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Laminar BL</text>

        {/* Transition marker */}
        <line x1={360} y1={140} x2={360} y2={248} stroke="#e07b00" strokeWidth="2" strokeDasharray="4,3"/>
        <text x={340} y={135} fontSize="12" fill="#e07b00" fontWeight="700">Transition</text>
        <text x={340} y={150} fontSize="11" fill="#e07b00">Rex ≈ 5×10⁵</text>

        {/* Turbulent region fill */}
        <path d="M 360,235 L 360,203 C 450,193 560,183 700,177 L 700,235 Z"
          fill="#f5b7b1" opacity="0.4"/>
        <text x={520} y={220} textAnchor="middle" fontSize="13" fill="#7B0000" fontWeight="700">Turbulent BL</text>

        {/* Velocity profiles at two stations */}
        {/* Laminar profile at x=200 */}
        <path d="M 200,235 C 220,225 235,210 235,200 L 235,200" fill="none" stroke="#27ae60" strokeWidth="2"/>
        <line x1={200} y1={200} x2={235} y2={200} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="3,2"/>
        <text x={238} y={203} fontSize="11" fill="#27ae60">u profile</text>

        {/* Turbulent profile at x=600 */}
        <path d="M 600,235 C 640,215 658,195 662,180 L 662,180" fill="none" stroke="#9b59b6" strokeWidth="2"/>
        <line x1={600} y1={180} x2={662} y2={180} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="3,2"/>
        <text x={665} y={183} fontSize="11" fill="#9b59b6">u profile</text>

        {/* No-slip label */}
        <text x={82} y={233} fontSize="11" fill="#c0392b">u = 0 at wall (no-slip)</text>

        {/* Key annotations */}
        <rect x={12} y={268} width={280} height={36} rx={3} fill="#fff5f5" stroke="#c0392b" strokeWidth="1"/>
        <text x={18} y={283} fontSize="11" fill="#7B0D0D" fontWeight="700">Laminar: δ ∝ x^(1/2) / Re_x^(1/2)</text>
        <text x={18} y={298} fontSize="11" fill="#444">Thickens as √x downstream</text>

        <rect x={310} y={268} width={280} height={36} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={316} y={283} fontSize="11" fill="#7B0000" fontWeight="700">Turbulent: δ ∝ x^(4/5)</text>
        <text x={316} y={298} fontSize="11" fill="#444">Thickens faster; fuller velocity profile</text>

        <rect x={610} y={268} width={278} height={36} rx={3} fill="#f5f0ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={616} y={283} fontSize="11" fill="#5b2c8d" fontWeight="700">Skin friction: Cf = τ_w / (½ρU∞²)</text>
        <text x={616} y={298} fontSize="11" fill="#444">Higher in turbulent BL; main source of friction drag</text>
      </svg>
      <div style={S.dCaption}>When fluid flows over a flat plate, a boundary layer forms from the leading edge as viscosity retards the flow. The layer grows in thickness δ(x) downstream. The flow is initially laminar, transitioning to turbulent at Re_x ≈ 5×10⁵. The no-slip condition (u = 0 at the wall) is fundamental — viscous shear stress τ_w = μ(du/dy)|_wall drives skin-friction drag.</div>
    </div>
  );
}

// ── Diagram 3: Flow Around a Cylinder — Pressure & Wake ───────────
function DiagramCylinderFlow() {
  const W = 900, H = 360;
  const cx = 380, cy = 180, R = 60;

  // Streamlines around cylinder — approximate
  const streamlines = [
    // Far top
    { d:`M 30,70 C 180,70 300,50 ${cx},${cy-R-80} C ${cx+80},${cy-R-80} ${cx+100},${cy-R-60} 700,70 L 870,70`, color:'#c0392b' },
    // Near top
    { d:`M 30,115 C 180,115 290,95 ${cx-20},${cy-R-30} C ${cx+15},${cy-R-35} ${cx+R+30},${cy-R-20} 700,115 L 870,115`, color:'#c0392b' },
    // Top separation
    { d:`M 30,150 C 160,150 280,140 ${cx-R-10},${cy-10} C ${cx-R+5},${cy} ${cx-R+5},${cy} ${cx-R-10},${cy+10} C 280,${cy+20} 450,${cy+25} 870,${cy-30}`, color:'#c0392b' },
    // Bottom separation
    { d:`M 30,210 C 160,210 280,220 ${cx-R-10},${cy+10} C ${cx-R+5},${cy} ${cx-R+5},${cy} ${cx-R-10},${cy-10} C 280,${cy-20} 450,${cy-25} 870,${cy+30}`, color:'#c0392b' },
    { d:`M 30,245 C 180,245 290,255 ${cx-20},${cy+R+30} C ${cx+15},${cy+R+35} ${cx+R+30},${cy+R+20} 700,245 L 870,245`, color:'#c0392b' },
    { d:`M 30,290 C 180,290 300,310 ${cx},${cy+R+80} C ${cx+80},${cy+R+80} ${cx+100},${cy+R+60} 700,290 L 870,290`, color:'#c0392b' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>External Flow Around a Cylinder — Streamlines, Pressure & Separation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="arrRed3" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b" opacity="0.7"/>
          </marker>
          <radialGradient id="cylGrad" cx="40%" cy="35%">
            <stop offset="0%" stopColor="#aaa"/>
            <stop offset="100%" stopColor="#555"/>
          </radialGradient>
        </defs>

        {/* Background */}
        <rect x={0} y={0} width={W} height={H} fill="#fafcff"/>

        {/* Incoming flow arrows */}
        {[70,115,150,180,210,245,290].map(y => (
          <line key={y} x1={10} y1={y} x2={32} y2={y} stroke="#c0392b"
            strokeWidth="1.3" markerEnd="url(#arrRed3)" opacity="0.4"/>
        ))}

        {/* Streamlines */}
        {streamlines.map((l, i) => (
          <path key={i} d={l.d} fill="none" stroke={l.color} strokeWidth="1.6" opacity="0.6" markerEnd="url(#arrRed3)"/>
        ))}

        {/* Cylinder */}
        <circle cx={cx} cy={cy} r={R} fill="url(#cylGrad)" stroke="#333" strokeWidth="2"/>

        {/* Stagnation points */}
        <circle cx={cx-R} cy={cy} r={5} fill="#c0392b"/>
        <text x={cx-R-10} y={cy-10} fontSize="12" fill="#c0392b" fontWeight="700" textAnchor="middle">S₁</text>
        <text x={cx-R-10} y={cy-22} fontSize="11" fill="#c0392b">Front stagnation</text>
        <text x={cx-R-10} y={cy-35} fontSize="11" fill="#c0392b">(p = p∞ + ½ρU²)</text>

        <circle cx={cx+R} cy={cy} r={5} fill="#27ae60"/>
        <text x={cx+R+10} y={cy-10} fontSize="12" fill="#27ae60" fontWeight="700">S₂</text>
        <text x={cx+R+12} y={cy-22} fontSize="11" fill="#27ae60">Rear stagnation</text>

        {/* Separation points */}
        <circle cx={cx+20} cy={cy-R} r={4} fill="#e07b00"/>
        <text x={cx+24} y={cy-R-8} fontSize="11" fill="#e07b00" fontWeight="700">Sep.</text>
        <circle cx={cx+20} cy={cy+R} r={4} fill="#e07b00"/>

        {/* Wake region */}
        <path d={`M ${cx+R-10},${cy-R+30} C ${cx+R+30},${cy-45} ${cx+R+120},${cy-35} ${cx+R+200},${cy-20}`}
          fill="none" stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.7"/>
        <path d={`M ${cx+R-10},${cy+R-30} C ${cx+R+30},${cy+45} ${cx+R+120},${cy+35} ${cx+R+200},${cy+20}`}
          fill="none" stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.7"/>
        <text x={cx+R+80} y={cy+6} fontSize="13" fill="#9b59b6" fontWeight="700">Wake / Recirculation</text>
        <text x={cx+R+80} y={cy+22} fontSize="11" fill="#9b59b6">Low pressure region</text>
        <text x={cx+R+80} y={cy+38} fontSize="11" fill="#9b59b6">→ pressure drag</text>

        {/* Pressure labels on cylinder */}
        <text x={cx} y={cy+4} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">Cylinder</text>

        {/* High/low pressure indicators */}
        <rect x={cx-R-90} y={cy+20} width={78} height={40} rx={3} fill="#bde8bd" stroke="#27ae60" strokeWidth="1"/>
        <text x={cx-R-51} y={cy+36} textAnchor="middle" fontSize="11" fill="#145214" fontWeight="700">HIGH pressure</text>
        <text x={cx-R-51} y={cy+52} textAnchor="middle" fontSize="11" fill="#145214">Front (stagnation)</text>

        <rect x={cx+R+10} y={cy+65} width={78} height={40} rx={3} fill="#f5d0d0" stroke="#c0392b" strokeWidth="1"/>
        <text x={cx+R+49} y={cy+81} textAnchor="middle" fontSize="11" fill="#7B0000" fontWeight="700">LOW pressure</text>
        <text x={cx+R+49} y={cy+97} textAnchor="middle" fontSize="11" fill="#7B0000">Rear (wake)</text>

        {/* Vortex shedding indicator */}
        <text x={cx+R+200} y={cy-50} fontSize="12" fill="#e07b00" fontWeight="700">Vortex shedding</text>
        <text x={cx+R+200} y={cy-36} fontSize="11" fill="#e07b00">(Strouhal: St = fD/U∞)</text>
        {[[720,140],[760,160],[800,145],[840,165]].map(([x,y],i)=>(
          <circle key={i} cx={x} cy={y} r={12+i*2} fill="none" stroke="#e07b00" strokeWidth="1.2"
            strokeDasharray="3,2" opacity={0.5-i*0.08}/>
        ))}
      </svg>
      <div style={S.dCaption}>Flow around a cylinder shows key external-flow features: flow accelerates around the sides (Bernoulli: higher speed → lower pressure), decelerates to stagnation at front and rear, and separates from the surface at the separation points. This creates an asymmetric pressure distribution — high at the front, low in the wake — generating pressure drag (form drag). At moderate Re, periodic vortex shedding occurs, characterised by the Strouhal number.</div>
    </div>
  );
}

// ── Diagram 4: Drag Forces — Pressure vs Friction + Aerofoil ──────
function DiagramDragComponents() {
  const W = 900, H = 340;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Drag Components — Pressure (Form) Drag vs Skin-Friction Drag</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left panel — Bluff body (sphere) */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={30} textAnchor="middle" fontSize="15" fill="#7B0D0D" fontWeight="700">Bluff Body (Sphere) — Pressure Drag Dominated</text>

        {(()=>{
          const scx = 10+PW/2-20, scy = 155, sr = 65;
          return <>
            {/* Sphere */}
            <circle cx={scx} cy={scy} r={sr} fill="#e8c8c8" stroke="#c0392b" strokeWidth="2"/>
            <text x={scx} y={scy+5} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Sphere</text>

            {/* Pressure arrows — high at front */}
            {[[-sr,0],[-sr+6,-25],[-sr+6,25]].map(([dx,dy],i)=>(
              <line key={i}
                x1={scx+dx-28+dx*0.2} y1={scy+dy}
                x2={scx+dx} y2={scy+dy}
                stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#pArr)"/>
            ))}
            <text x={scx-sr-55} y={scy} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">High p</text>

            {/* Pressure arrows — low at rear */}
            {[[sr,0],[sr-6,-20],[sr-6,20]].map(([dx,dy],i)=>(
              <line key={i}
                x1={scx+dx} y1={scy+dy}
                x2={scx+dx+25} y2={scy+dy}
                stroke="#9b59b6" strokeWidth="2.5" markerEnd="url(#pArrV)"/>
            ))}
            <text x={scx+sr+45} y={scy} textAnchor="middle" fontSize="11" fill="#9b59b6" fontWeight="700">Low p</text>

            {/* Net drag arrow */}
            <line x1={scx} y1={scy-sr-25} x2={scx+70} y2={scy-sr-25} stroke="#c0392b" strokeWidth="3" markerEnd="url(#pArr)"/>
            <text x={scx+75} y={scy-sr-20} fontSize="13" fill="#c0392b" fontWeight="700">F_D (drag)</text>

            {/* Percentages */}
            <rect x={16} y={260} width={PW-12} height={62} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={22} y={276} fontSize="12" fill="#7B0000" fontWeight="700">Pressure drag ≈ 90%  |  Friction drag ≈ 10%</text>
            <text x={22} y={292} fontSize="11" fill="#444">Large wake → big low-pressure region behind body</text>
            <text x={22} y={308} fontSize="11" fill="#444">C_D(sphere) ≈ 0.47 at Re ~ 10⁵</text>
            <text x={22} y={320} fontSize="11" fill="#444">Streamlining can reduce C_D by up to 10×</text>
          </>;
        })()}

        {/* Arrow markers */}
        <defs>
          <marker id="pArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="pArrV" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#9b59b6"/>
          </marker>
          <marker id="pArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Right panel — Streamlined aerofoil */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={30} textAnchor="middle" fontSize="15" fill="#7B0D0D" fontWeight="700">Streamlined Body (Aerofoil) — Friction Drag Dominated</text>

        {(()=>{
          const ax = PW+50, ay = 130;
          // Aerofoil shape
          const aeroPath = `M ${ax},${ay+25} C ${ax+40},${ay+25} ${ax+100},${ay+18} ${ax+200},${ay+25}
            C ${ax+240},${ay+27} ${ax+260},${ay+28} ${ax+280},${ay+25}
            C ${ax+260},${ay+22} ${ax+240},${ay+20} ${ax+200},${ay+22}
            C ${ax+100},${ay+15} ${ax+40},${ay+10} ${ax},${ay+25} Z`;

          return <>
            <path d={aeroPath} fill="#e8c8c8" stroke="#c0392b" strokeWidth="2"/>
            <text x={ax+140} y={ay+27} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Aerofoil</text>

            {/* Streamlines around aerofoil */}
            {[[-30,-5,0],[0,0,0],[30,5,0],[-25,-35,-15],[25,35,15]].map(([dy1,dy2,dy3],i) => (
              <path key={i}
                d={`M ${ax-60},${ay+25+dy1} C ${ax+50},${ay+25+dy1} ${ax+140},${ay+20+dy2} ${ax+350},${ay+25+dy3}`}
                fill="none" stroke="#c0392b" strokeWidth="1.3" opacity="0.45"/>
            ))}

            {/* Minimal wake */}
            <path d={`M ${ax+280},${ay+24} C ${ax+310},${ay+22} ${ax+340},${ay+24} ${ax+370},${ay+25}`}
              fill="none" stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.6"/>
            <text x={ax+330} y={ay+16} fontSize="11" fill="#9b59b6">Tiny wake</text>

            {/* Friction arrows along surface */}
            {[60,110,160,200,240].map((dx,i) => (
              <line key={i}
                x1={ax+dx} y1={ay+10}
                x2={ax+dx+18} y2={ay+10}
                stroke="#27ae60" strokeWidth="1.8" markerEnd="url(#pArrG)" opacity="0.7"/>
            ))}
            <text x={ax+80} y={ay+4} fontSize="11" fill="#27ae60" fontWeight="700">Skin friction τ_w along surface</text>

            <rect x={PW+26} y={260} width={PW-12} height={62} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
            <text x={PW+32} y={276} fontSize="12" fill="#145214" fontWeight="700">Pressure drag ≈ 5–10%  |  Friction drag ≈ 90–95%</text>
            <text x={PW+32} y={292} fontSize="11" fill="#444">Streamlined shape prevents separation → tiny wake</text>
            <text x={PW+32} y={308} fontSize="11" fill="#444">C_D(aerofoil) ≈ 0.005–0.01  (much lower than sphere!)</text>
            <text x={PW+32} y={320} fontSize="11" fill="#444">Also generates lift: C_L up to ~1.5 at cruise conditions</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Total drag = pressure (form) drag + skin-friction drag. Bluff bodies (spheres, cylinders) have large separated wakes — most drag is due to the pressure imbalance front-to-rear. Streamlined bodies (aerofoils) avoid separation — drag is dominated by viscous skin friction. The drag coefficient C_D = F_D / (½ρU²A) quantifies drag independently of size and speed.</div>
    </div>
  );
}

// ── Diagram 5: Lift on an Aerofoil — Bernoulli & Circulation ──────
function DiagramAerofoilLift() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Aerofoil — Lift Generation, Angle of Attack & Stall</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="lArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="lArrR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="lArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
        </defs>

        {/* Main aerofoil */}
        {(()=>{
          const ax = 120, ay = 160, chord = 300, t = 38;
          // NACA-like shape: asymmetric
          const upper = `M ${ax},${ay} C ${ax+80},${ay-t} ${ax+180},${ay-t-8} ${ax+chord},${ay+8}`;
          const lower = `M ${ax+chord},${ay+8} C ${ax+200},${ay+20} ${ax+100},${ay+18} ${ax},${ay} Z`;
          return <>
            <path d={upper + ' ' + lower} fill="#e8c8c8" stroke="#c0392b" strokeWidth="2"/>

            {/* Angle of attack arc */}
            <path d={`M ${ax+40},${ay} A 40 40 0 0 0 ${ax+40-20},${ay-20}`}
              fill="none" stroke="#e07b00" strokeWidth="1.5"/>
            <text x={ax+48} y={ay+14} fontSize="11" fill="#e07b00" fontWeight="700">α (AoA)</text>

            {/* Chord line */}
            <line x1={ax} y1={ay} x2={ax+chord} y2={ay+8} stroke="#555" strokeWidth="1" strokeDasharray="4,3"/>
            <text x={ax+chord/2} y={ay+22} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">chord line</text>

            {/* Upper streamlines — compressed, faster */}
            {[-35,-60,-85].map((dy,i) => (
              <path key={i}
                d={`M 30,${ay+dy+10} C ${ax-10},${ay+dy} ${ax+50},${ay-t-5+dy/3} ${ax+chord+30},${ay+dy/2}`}
                fill="none" stroke="#c0392b" strokeWidth={i===0?"2":"1.3"} opacity={0.5+i*0.1}
                markerEnd="url(#lArrB)"/>
            ))}
            <text x={ax+50} y={ay-t-22} fontSize="12" fill="#c0392b" fontWeight="700">Faster flow (↑ velocity)</text>
            <text x={ax+50} y={ay-t-8} fontSize="11" fill="#c0392b" fontWeight="700">→ Lower pressure (Bernoulli)</text>

            {/* Lower streamlines — spread, slower */}
            {[25,50,75].map((dy,i) => (
              <path key={i}
                d={`M 30,${ay+dy+8} C ${ax+30},${ay+dy+4} ${ax+120},${ay+dy-4} ${ax+chord+30},${ay+dy}`}
                fill="none" stroke="#c0392b" strokeWidth="1.3" opacity={0.4+i*0.05}
                markerEnd="url(#lArrB)"/>
            ))}
            <text x={ax+50} y={ay+68} fontSize="12" fill="#c0392b" fontWeight="700">Slower flow (↓ velocity)</text>
            <text x={ax+50} y={ay+82} fontSize="11" fill="#27ae60" fontWeight="700">→ Higher pressure (Bernoulli)</text>

            {/* Lift arrow */}
            <line x1={ax+chord/2} y1={ay-t+5} x2={ax+chord/2} y2={ay-t-70}
              stroke="#27ae60" strokeWidth="3.5" markerEnd="url(#lArr)"/>
            <text x={ax+chord/2+8} y={ay-t-45} fontSize="14" fill="#27ae60" fontWeight="700">LIFT</text>
            <text x={ax+chord/2+8} y={ay-t-30} fontSize="11" fill="#27ae60">L = ½ρU²S·C_L</text>

            {/* Drag arrow */}
            <line x1={ax+chord+10} y1={ay+4} x2={ax+chord+80} y2={ay+4}
              stroke="#c0392b" strokeWidth="3" markerEnd="url(#lArrR)"/>
            <text x={ax+chord+85} y={ay+8} fontSize="13" fill="#c0392b" fontWeight="700">DRAG</text>

            {/* Free stream arrow */}
            <line x1={20} y1={ay} x2={ax-12} y2={ay}
              stroke="#555" strokeWidth="2.5" markerEnd="url(#lArrB)"/>
            <text x={20} y={ay-8} fontSize="12" fill="#555" fontWeight="700">U∞</text>
          </>;
        })()}

        {/* Right panel — Stall diagram */}
        <rect x={600} y={10} width={290} height={320} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={745} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">C_L vs Angle of Attack</text>

        {/* Axes */}
        <line x1={620} y1={290} x2={620} y2={40} stroke="#333" strokeWidth="1.5"/>
        <line x1={620} y1={290} x2={880} y2={290} stroke="#333" strokeWidth="1.5"/>
        <text x={610} y={36} fontSize="11" fill="#333">C_L</text>
        <text x={882} y={294} fontSize="11" fill="#333">α</text>

        {/* C_L curve — rises linearly then drops sharply (stall) */}
        <path d="M 635,280 C 670,260 700,230 730,200 C 755,174 768,160 780,152 C 790,148 800,148 810,152 C 820,158 825,168 835,190 C 845,210 855,240 870,265"
          fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Stall point */}
        <circle cx={810} cy={152} r={5} fill="#c0392b"/>
        <line x1={810} y1={152} x2={810} y2={290} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={812} y={145} fontSize="11" fill="#c0392b" fontWeight="700">STALL</text>
        <text x={812} y={132} fontSize="11" fill="#c0392b">α ≈ 15°</text>

        {/* Axis labels */}
        <text x={635} y={300} fontSize="10" fill="#555">0°</text>
        <text x={680} y={300} fontSize="10" fill="#555">5°</text>
        <text x={730} y={300} fontSize="10" fill="#555">10°</text>
        <text x={778} y={300} fontSize="10" fill="#555">15°</text>
        <text x={830} y={300} fontSize="10" fill="#555">20°</text>
        <text x={612} y={200} fontSize="10" fill="#555">1.0</text>
        <text x={612} y={155} fontSize="10" fill="#555">1.5</text>

        {/* Separation annotation */}
        <text x={838} y={210} fontSize="10" fill="#c0392b">Flow</text>
        <text x={838} y={222} fontSize="10" fill="#c0392b">separation</text>
        <text x={838} y={234} fontSize="10" fill="#c0392b">→ stall</text>

        {/* Labels */}
        <text x={660} y={245} fontSize="10" fill="#c0392b">Linear</text>
        <text x={660} y={257} fontSize="10" fill="#c0392b">region</text>
      </svg>
      <div style={S.dCaption}>An aerofoil generates lift because its cambered shape and angle of attack (α) cause the upper surface flow to accelerate, reducing pressure (Bernoulli's principle). The net upward pressure difference gives lift: L = ½ρU²S·C_L. As α increases, C_L rises linearly — until the boundary layer separates, causing a sudden loss of lift: the stall, typically at α ≈ 15°.</div>
    </div>
  );
}

// ── Diagram 6: Drag Coefficient vs Reynolds Number ────────────────
function DiagramCdReynolds() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Drag Coefficient C_D vs Reynolds Number — Key Flow Regimes</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {/* Axes */}
        <line x1={70} y1={280} x2={860} y2={280} stroke="#333" strokeWidth="1.5"/>
        <line x1={70} y1={280} x2={70} y2={20} stroke="#333" strokeWidth="1.5"/>

        {/* Axis labels */}
        <text x={460} y={308} textAnchor="middle" fontSize="13" fill="#333" fontWeight="700">Reynolds Number (log scale)</text>
        <text x={22} y={160} fontSize="13" fill="#333" fontWeight="700"
          transform="rotate(-90, 22, 160)">C_D (drag coefficient)</text>

        {/* X tick labels (log scale approximate) */}
        {[
          [80,'10⁻²'],[150,'10⁻¹'],[220,'10⁰'],[290,'10¹'],[360,'10²'],
          [430,'10³'],[500,'10⁴'],[570,'10⁵'],[640,'10⁶'],[710,'10⁷']
        ].map(([x,label]) => (
          <g key={x}>
            <line x1={x} y1={277} x2={x} y2={283} stroke="#333" strokeWidth="1"/>
            <text x={x} y={296} textAnchor="middle" fontSize="10" fill="#555">{label}</text>
          </g>
        ))}

        {/* Y ticks */}
        {[[240,'1'],[200,'10'],[160,'100'],[120,'1000']].map(([y,label]) => (
          <g key={y}>
            <line x1={67} y1={y} x2={73} y2={y} stroke="#333" strokeWidth="1"/>
            <text x={62} y={y+4} textAnchor="end" fontSize="10" fill="#555">{label}</text>
          </g>
        ))}

        {/* C_D curve for sphere — Stokes → intermediate → plateau → drag crisis */}
        {/* Stokes: slope -1 on log-log from Re~0.01 to ~1 */}
        <path d="M 80,48 C 100,60 130,80 150,100 C 175,120 205,150 220,168 C 250,195 280,220 290,230 C 310,238 340,240 360,240 C 400,240 430,240 460,240 C 490,240 520,240 540,240 C 550,240 560,240 570,238 C 580,235 590,230 600,225 C 615,215 625,195 635,198 C 645,200 660,210 680,218 C 700,225 720,232 750,238 C 770,242 800,244 850,244"
          fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Drag crisis drop at Re~5×10⁵ */}
        <path d="M 570,238 C 580,235 590,220 598,200 C 606,185 615,195 625,195"
          fill="none" stroke="#c0392b" strokeWidth="2.5"/>
        <circle cx={590} cy={217} r={5} fill="#c0392b"/>
        <text x={596} y={210} fontSize="11" fill="#c0392b" fontWeight="700">Drag crisis</text>
        <text x={596} y={222} fontSize="11" fill="#c0392b">BL turns turbulent</text>
        <text x={596} y={234} fontSize="11" fill="#c0392b">→ C_D drops sharply</text>

        {/* Region annotations */}
        {/* Stokes flow */}
        <rect x={76} y={24} width={100} height={36} rx={3} fill="#fff5f5" stroke="#c0392b" strokeWidth="1" opacity="0.9"/>
        <text x={80} y={39} fontSize="11" fill="#7B0D0D" fontWeight="700">Stokes flow</text>
        <text x={80} y={53} fontSize="10" fill="#444">C_D = 24/Re</text>

        {/* Intermediate */}
        <rect x={280} y={60} width={110} height={36} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1" opacity="0.9"/>
        <text x={284} y={75} fontSize="11" fill="#8B4513" fontWeight="700">Intermediate</text>
        <text x={284} y={89} fontSize="10" fill="#444">C_D ∝ Re^(-0.5)</text>

        {/* Newton's plateau */}
        <rect x={430} y={215} width={115} height={36} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1" opacity="0.9"/>
        <text x={434} y={230} fontSize="11" fill="#145214" fontWeight="700">Newton's plateau</text>
        <text x={434} y={244} fontSize="10" fill="#444">C_D ≈ 0.44 (const.)</text>

        {/* Legend */}
        <rect x={680} y={20} width={170} height={40} rx={4} fill="#fff" stroke="#f0cece"/>
        <line x1={690} y1={36} x2={720} y2={36} stroke="#c0392b" strokeWidth="2.5"/>
        <text x={726} y={40} fontSize="12" fill="#7B0D0D">C_D for a sphere</text>
        <text x={726} y={54} fontSize="10" fill="#555">(standard drag curve)</text>
      </svg>
      <div style={S.dCaption}>The drag coefficient C_D of a sphere varies dramatically with Reynolds number. At very low Re (Stokes flow), C_D = 24/Re — viscosity dominates. At moderate Re, C_D gradually decreases. Between Re ≈ 10³–2×10⁵ it plateaus near 0.44 (pressure drag dominates). At Re ≈ 5×10⁵, the laminar boundary layer transitions to turbulent — it stays attached longer, reducing the wake and causing a sudden drop in C_D: the drag crisis exploited by golf ball dimples.</div>
    </div>
  );
}

// ── Diagram 7: Magnus Effect & Flow Control ────────────────────────
function DiagramMagnus() {
  const W = 900, H = 300;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Magnus Effect — Spinning Body in a Flow</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        <defs>
          <marker id="mArr" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#c0392b" opacity="0.7"/>
          </marker>
          <marker id="mArrG" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
            <polygon points="0 0, 5 3, 0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Left: Non-spinning cylinder */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">No Spin — Symmetric Flow, Zero Lift</text>

        {(()=>{
          const cx = 10+PW/2, cy = 155, r = 55;
          // Symmetric streamlines top and bottom
          const lines = [
            [60,'top'],[85,'top'],[105,'top'],
            [60,'bot'],[85,'bot'],[105,'bot'],
          ];
          return <>
            {[60,85,105].map((d,i) => <>
              <path key={`t${i}`}
                d={`M 20,${cy-d} C 120,${cy-d} 260,${cy-r-d*0.3} ${cx},${cy-r} C ${cx+80},${cy-r} ${cx+120},${cy-d*0.4} 430,${cy-d}`}
                fill="none" stroke="#c0392b" strokeWidth="1.4" opacity="0.5" markerEnd="url(#mArr)"/>
              <path key={`b${i}`}
                d={`M 20,${cy+d} C 120,${cy+d} 260,${cy+r+d*0.3} ${cx},${cy+r} C ${cx+80},${cy+r} ${cx+120},${cy+d*0.4} 430,${cy+d}`}
                fill="none" stroke="#c0392b" strokeWidth="1.4" opacity="0.5" markerEnd="url(#mArr)"/>
            </>)}
            <circle cx={cx} cy={cy} r={r} fill="#e8c8c8" stroke="#c0392b" strokeWidth="2"/>
            <text x={cx} y={cy+5} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Cylinder</text>
            {/* Symmetric pressure */}
            <text x={cx} y={cy-r-10} textAnchor="middle" fontSize="11" fill="#c0392b">Low p</text>
            <text x={cx} y={cy+r+18} textAnchor="middle" fontSize="11" fill="#c0392b">Low p</text>
            <text x={20} y={cy+4} fontSize="11" fill="#27ae60">High p</text>
            <text x={cx+r+5} y={cy+4} fontSize="11" fill="#27ae60">High p</text>
            {/* No lift */}
            <text x={cx} y={cy-r-30} textAnchor="middle" fontSize="12" fill="#888">Net lift = 0 (symmetric)</text>
          </>;
        })()}

        {/* Right: Spinning cylinder — Magnus */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Spinning — Magnus Effect → Lift</text>

        {(()=>{
          const cx = PW+20+PW/2, cy = 155, r = 55;
          return <>
            {/* Upper streamlines — compressed (faster) */}
            {[50,70,90].map((d,i) => (
              <path key={`t${i}`}
                d={`M ${PW+30},${cy-d-20} C ${cx-80},${cy-d-25} ${cx-20},${cy-r-20} ${cx},${cy-r} C ${cx+20},${cy-r-18} ${cx+80},${cy-d-20} 875,${cy-d-15}`}
                fill="none" stroke="#c0392b" strokeWidth="1.4" opacity="0.55" markerEnd="url(#mArr)"/>
            ))}
            {/* Lower streamlines — spread (slower) */}
            {[50,70,90].map((d,i) => (
              <path key={`b${i}`}
                d={`M ${PW+30},${cy+d+10} C ${cx-80},${cy+d+15} ${cx-20},${cy+r+15} ${cx},${cy+r} C ${cx+20},${cy+r+12} ${cx+80},${cy+d+8} 875,${cy+d+6}`}
                fill="none" stroke="#c0392b" strokeWidth="1.4" opacity="0.45" markerEnd="url(#mArr)"/>
            ))}

            <circle cx={cx} cy={cy} r={r} fill="#e8c8c8" stroke="#c0392b" strokeWidth="2"/>
            {/* Spin arrows */}
            <path d={`M ${cx+r-8},${cy-20} A ${r-8} ${r-8} 0 0 0 ${cx-r+8},${cy-20}`}
              fill="none" stroke="#e07b00" strokeWidth="2.5" markerEnd="url(#mArr)"/>
            <text x={cx} y={cy+8} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">ω (spin)</text>

            {/* Pressure labels */}
            <text x={cx} y={cy-r-12} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Very low p (fast flow)</text>
            <text x={cx} y={cy+r+18} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">Higher p (slow flow)</text>

            {/* Lift arrow */}
            <line x1={cx} y1={cy-r-20} x2={cx} y2={cy-r-70}
              stroke="#27ae60" strokeWidth="3.5" markerEnd="url(#mArrG)"/>
            <text x={cx+8} y={cy-r-48} fontSize="14" fill="#27ae60" fontWeight="700">Magnus Lift</text>
            <text x={cx+8} y={cy-r-33} fontSize="11" fill="#27ae60">F = ρU∞ΓL</text>
            <text x={cx+8} y={cy-r-18} fontSize="11" fill="#27ae60">(Γ = circulation)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>The Magnus effect: a spinning cylinder (or ball) in a flow experiences a lift force perpendicular to the flow. Spin adds velocity on one side (combining with flow) and subtracts on the other — Bernoulli gives a pressure difference and hence a net force. This explains why a spinning football curves, a cricket ball swings, and a topspin tennis ball dips. The lift force is F = ρU∞ΓL, where Γ is the circulation (∮ v·dl around the body).</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────
export default function ExternalFlowDrag() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>External Flow &amp; Drag in Fluid Dynamics</h1>
      <p style={S.p}>
        When a fluid flows over or around an object — a wing, a car, a ball, a bridge pier — the interaction between the fluid and the body's surface determines the forces of lift and drag. Understanding external flow is fundamental to aeronautics, naval architecture, civil engineering, and even sports science. The behaviour depends critically on the fluid's viscosity, density, and speed, captured in a single dimensionless number: the Reynolds number.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "Reynolds number Re = ρvL/μ governs whether flow is laminar (smooth) or turbulent (chaotic)",
        "No-slip condition: fluid velocity is zero at a solid wall — viscosity creates a boundary layer",
        "Drag = pressure (form) drag + skin-friction drag; total: F_D = ½ρU²A·C_D",
        "Bernoulli's principle: faster flow → lower pressure; underpins lift generation on aerofoils",
        "Boundary layer separation creates a large turbulent wake → dominates drag on bluff bodies",
        "The drag crisis (Re ≈ 5×10⁵): BL transition to turbulent delays separation → C_D drops sharply",
        "Magnus effect: spinning bodies experience a lift force due to flow asymmetry (Kutta–Joukowski: F = ρU∞Γ)",
        "Strouhal number St = fD/U∞ describes periodic vortex shedding behind bluff bodies",
      ]}/>

      <h2 style={S.h2}>Flow Regimes — Laminar and Turbulent</h2>
      <p style={S.p}>
        The most fundamental classification of fluid flow is whether it is laminar or turbulent. In laminar flow, fluid moves in smooth, parallel layers with no mixing between them. In turbulent flow, the motion is chaotic — fluid particles follow irregular, three-dimensional paths with continuous lateral mixing. The transition between regimes is governed by the dimensionless Reynolds number, which measures the ratio of inertial forces to viscous forces.
      </p>
      <DiagramFlowRegimes/>
      <Table2 rows={[
        ["Reynolds number","Re = ρvL/μ = vL/ν\nρ = density (kg/m³), v = velocity (m/s), L = length scale (m), μ = dynamic viscosity (Pa·s)\nν = μ/ρ = kinematic viscosity (m²/s)"],
        ["Laminar (Re < 2300)","Smooth, ordered flow; viscous forces dominate\nNo lateral mixing; parabolic velocity profile in a pipe"],
        ["Transitional (2300–4000)","Unstable, intermittent — can flip between laminar and turbulent"],
        ["Turbulent (Re > 4000)","Chaotic eddies, high mixing, flatter velocity profile\nHigher momentum transfer to walls → more skin friction"],
        ["Significance","Turbulent BL is harder to separate (more energised) → can reduce pressure drag\nBut creates more skin friction — trade-off depends on body shape"],
      ]}/>

      <h2 style={S.h2}>The Boundary Layer</h2>
      <p style={S.p}>
        The no-slip condition — that fluid must have zero velocity at a solid surface — creates a thin region near the wall called the boundary layer, in which velocity transitions from zero at the wall to the free-stream value U∞. Outside the boundary layer, viscous effects are negligible and the flow can often be treated as inviscid. The boundary layer grows in thickness downstream and can be laminar or turbulent.
      </p>
      <DiagramBoundaryLayer/>
      <Table2 rows={[
        ["No-slip condition","u = 0 at the wall — fluid molecules are dragged along by the surface\nThis gradient du/dy creates shear stress: τ = μ(du/dy)"],
        ["Boundary layer thickness δ","Defined as the distance from wall where u = 0.99 U∞\nLaminar flat plate: δ = 5x / Re_x^(1/2)"],
        ["Displacement thickness δ*","δ* = ∫₀^∞ (1 - u/U∞) dy\nMeasures how much the outer flow is displaced by the BL"],
        ["Momentum thickness θ","θ = ∫₀^∞ (u/U∞)(1 - u/U∞) dy\nDirectly linked to skin friction drag via von Kármán equation"],
        ["Shape factor H","H = δ*/θ; laminar H ≈ 2.6, turbulent H ≈ 1.3–1.4\nH increases toward separation; H → ∞ at separation point"],
        ["Skin friction coefficient","Cf = τ_w / (½ρU∞²)\nLaminar BL: Cf = 0.664/Re_x^(1/2);  Turbulent: Cf ≈ 0.0592/Re_x^(1/5)"],
        ["BL separation","Occurs when the adverse pressure gradient (∂p/∂x > 0) decelerates\nfluid near the wall to zero velocity — detachment forms a wake"],
      ]}/>

      <h2 style={S.h2}>Flow Around a Cylinder — Separation and Vortex Shedding</h2>
      <p style={S.p}>
        The flow around a bluff body such as a cylinder illustrates all the key features of external flow: acceleration around the sides, stagnation at front and rear, boundary layer separation, and the formation of a turbulent wake. The pressure imbalance between the high-pressure front and low-pressure wake creates pressure drag.
      </p>
      <DiagramCylinderFlow/>
      <Table2 rows={[
        ["Stagnation point","Where the streamline hits the body head-on; v = 0, p = p∞ + ½ρU²\nFront stagnation: highest pressure. Rear stagnation (ideal): same pressure"],
        ["Pressure coefficient","Cp = (p - p∞) / (½ρU∞²)\nIdeal flow: Cp = 1 - 4sin²θ (fore-aft symmetric — no drag: d'Alembert's paradox)"],
        ["D'Alembert's paradox","Ideal (inviscid) theory predicts zero drag on a body — contradicts reality\nViscosity, separation, and wake break the symmetry → real drag exists"],
        ["Separation angle","Laminar BL: separation at θ ≈ 82° from front stagnation\nTurbulent BL: separation delayed to θ ≈ 120° → smaller wake → less drag"],
        ["Vortex shedding","Alternating vortices shed from top and bottom of body\nKármán vortex street — can excite resonance in structures (bridges, chimneys)"],
        ["Strouhal number","St = fD/U∞  (f = shedding frequency, D = diameter)\nSt ≈ 0.2 for a cylinder over wide Re range"],
        ["Drag coefficient cylinder","C_D ≈ 1.0–1.2 in subcritical regime (10³ < Re < 2×10⁵)\nDrops to ~0.3 in supercritical regime after drag crisis"],
      ]}/>

      <h2 style={S.h2}>Drag Forces — Components and Streamlining</h2>
      <p style={S.p}>
        The total drag force on a body is the sum of pressure drag (form drag) and skin-friction drag. Which dominates depends entirely on the body shape. A bluff body (sphere, flat plate normal to flow) has massive flow separation and its drag is almost entirely from the pressure imbalance. A well-streamlined body (aerofoil, teardrop) avoids separation and its drag is mostly skin friction.
      </p>
      <DiagramDragComponents/>
      <Table2 rows={[
        ["Total drag","F_D = F_pressure + F_friction\nF_D = ½ρU²A · C_D  (A = reference area, usually frontal area)"],
        ["Drag coefficient C_D","Dimensionless; depends on body shape and Re\nSphere ≈ 0.47, Cylinder ≈ 1.0, Flat plate (normal) ≈ 1.28\nStreamlined aerofoil ≈ 0.005–0.01"],
        ["Pressure (form) drag","Caused by pressure difference front-to-rear\nDominant in bluff bodies with large wakes — reduced by streamlining"],
        ["Skin-friction drag","Caused by shear stress τ_w along the surface\nDominant in thin, streamlined bodies — reduced by keeping BL laminar"],
        ["Parasitic drag (aircraft)","Sum of all drag except lift-induced drag\nIncludes skin friction, form drag, interference drag between components"],
        ["Induced drag","Drag arising from the generation of lift (3D wings)\nC_Di = C_L² / (π·AR·e); reduced by high aspect ratio AR and elliptic loading"],
        ["Streamlining principle","Extending the rear of a body in a teardrop shape delays/prevents separation\nCan reduce C_D by factor 10–20 compared with a bluff body of same frontal area"],
      ]}/>

      <h2 style={S.h2}>Aerofoil Aerodynamics — Lift, Angle of Attack, and Stall</h2>
      <p style={S.p}>
        An aerofoil (wing cross-section) is the classic example of a streamlined body designed to produce lift with minimal drag. Lift arises because the cambered shape and angle of attack deflect the airflow, creating a pressure difference between upper and lower surfaces. The Kutta condition — that flow leaves the sharp trailing edge smoothly — fixes the amount of circulation and hence lift.
      </p>
      <DiagramAerofoilLift/>
      <Table2 rows={[
        ["Lift force","L = ½ρU²S · C_L\nS = planform (wing) area, C_L = lift coefficient"],
        ["Kutta–Joukowski theorem","L = ρU∞Γ per unit span   (Γ = circulation = ∮ v·dl)\nLift equals fluid density × free-stream speed × circulation"],
        ["Lift coefficient C_L","C_L = dC_L/dα · α  (linear regime)\ndC_L/dα ≈ 2π per radian (thin aerofoil theory); real values slightly less"],
        ["Angle of attack α","Angle between chord line and incoming flow\nLift increases linearly with α up to the stall angle (~15°)"],
        ["Stall","At high α, adverse pressure gradient too steep → BL separates from upper surface\nC_L drops suddenly; C_D rises sharply — dangerous loss of lift"],
        ["Lift-to-drag ratio","L/D = C_L/C_D — key efficiency metric\nGlider: L/D ≈ 40; commercial airliner cruise: L/D ≈ 17–19"],
        ["High-lift devices","Flaps and slats increase C_L_max by extending chord and camber\nAllows lower stall speed for take-off and landing"],
        ["Winglets","Vertical tips reduce strength of tip vortices → reduce induced drag\nC_Di reduction of 3–5% on commercial aircraft"],
      ]}/>

      <h2 style={S.h2}>The Drag Coefficient Curve and the Drag Crisis</h2>
      <p style={S.p}>
        One of the most striking phenomena in external flow is the drag crisis: at a critical Reynolds number near 5×10⁵, the drag coefficient of a sphere drops by roughly 50% almost instantaneously. This occurs because the laminar boundary layer transitions to turbulent — which has more momentum near the wall and can resist the adverse pressure gradient for longer, delaying separation and shrinking the wake dramatically.
      </p>
      <DiagramCdReynolds/>
      <Table2 rows={[
        ["Stokes drag (Re ≪ 1)","C_D = 24/Re\nF_D = 3πμDU  (Stokes' law — viscosity dominates completely)\nUsed for particles in sedimentation, aerosols"],
        ["Oseen correction","C_D ≈ 24/Re · (1 + 3Re/16)  for Re up to ~5\nPartially accounts for inertia effects"],
        ["Intermediate range","C_D decreases as ~Re^(-0.5) — both viscosity and inertia matter\nInertia begins to dominate the wake structure"],
        ["Newton's plateau (10³–2×10⁵)","C_D ≈ 0.44 — roughly constant\nPressure drag dominates; turbulent wake fills most of rear"],
        ["Drag crisis (~5×10⁵)","Laminar BL → turbulent; separation delayed from 82° to ~120°\nC_D drops from ~0.5 to ~0.1–0.2 for a sphere"],
        ["Golf ball dimples","Dimples trip BL turbulent at lower Re → exploit drag crisis\nReduce C_D from ~0.47 to ~0.25 → roughly doubles range"],
        ["Supercritical regime","C_D gradually rises again as wake turbulence increases\nDifferent body shapes have different critical Re values"],
      ]}/>

      <h2 style={S.h2}>The Magnus Effect and Flow-Induced Forces</h2>
      <p style={S.p}>
        When a body rotates in a flow, the spin adds velocity on one side and subtracts it on the other, breaking the flow symmetry. By Bernoulli's principle, this creates a pressure asymmetry and a lateral force — the Magnus effect. This explains curved flight paths in ball sports and is exploited in Flettner rotors (spinning cylinders used as ship sails).
      </p>
      <DiagramMagnus/>
      <Table2 rows={[
        ["Magnus force","F_L = ρU∞ΓL  (per unit span)\nΓ = circulation = 2πRω for a spinning cylinder (R = radius, ω = angular velocity)"],
        ["Spin parameter","Sp = Rω/U∞  (ratio of surface speed to free-stream speed)\nHigher spin → more circulation → more Magnus lift"],
        ["Football curve","Topspin: deflects downward; backspin: floats upward; sidespin: curves laterally\nTypical ball: F_Magnus can exceed weight at high spin rates"],
        ["Cricket ball swing","Late swing: turbulent BL on seam side separates later → pressure asymmetry\nCombination of seam angle, polish, and speed → swing even without spin"],
        ["Flettner rotor ship","Tall spinning cylinders replace sails — Magnus force propels vessel\nEnergy-efficient auxiliary propulsion; revived in modern shipping"],
        ["Vortex-induced vibration","Alternating vortex shedding creates oscillating forces on structures\nCan cause resonance — failure of Tacoma Narrows Bridge (1940)"],
        ["Strouhal resonance","When f_shedding = f_natural → lock-in → large amplitude oscillation\nMitigated by helical strakes on chimneys, cables, offshore risers"],
      ]}/>

      <h2 style={S.h2}>Dimensional Analysis and Similarity</h2>
      <p style={S.p}>
        Dimensional analysis (the Buckingham π theorem) is the cornerstone of experimental fluid mechanics. It allows results from scale models to be applied to full-scale objects, provided the key dimensionless parameters match. Wind tunnel and water tunnel testing relies entirely on this principle.
      </p>
      <Table2 rows={[
        ["Buckingham π theorem","If a problem has n variables and k fundamental dimensions,\nthere are n – k independent dimensionless groups"],
        ["Reynolds similarity","For the same Re, flow patterns are geometrically similar regardless of fluid or size\nDouble the model size → halve the tunnel speed for the same Re"],
        ["Froude number","Fr = U / √(gL)  — ratio of inertia to gravity\nCritical for free-surface flows (ships, open channels, waves)"],
        ["Mach number","Ma = U / a  (a = speed of sound)\nCompressibility effects important when Ma > 0.3; aerodynamic forces change fundamentally at Ma = 1"],
        ["Euler number","Eu = ΔP / (ρU²)  — pressure coefficient\nRelevant for pressure-dominated flows; related to C_D and C_L"],
        ["Strouhal number","St = fL/U  — unsteady oscillation relative to convection\nSt ≈ 0.2 for vortex shedding behind bluff bodies across a wide Re range"],
        ["Wind tunnel testing","Model scaled so that Re (and Ma if relevant) matches full scale\nForces measured on model then scaled: F_full = F_model × (ρ_full/ρ_model)(U_full/U_model)²(L_full/L_model)²"],
      ]}/>

      <h2 style={S.h2}>Practical Applications</h2>
      <Table2 rows={[
        ["Aircraft wing design","High L/D ratio by careful camber, twist distribution, and winglets\nManage BL transition with smooth surfaces; high-lift devices for low-speed phases"],
        ["Car aerodynamics","Minimise drag (fuel efficiency) and control lift/downforce (stability)\nActive diffusers, splitters, and spoilers in motorsport; C_D ≈ 0.22–0.30 for production cars"],
        ["Tall structures","Chimneys, bridges, and skyscrapers designed to avoid vortex-shedding resonance\nHelical strakes, tuned mass dampers, or aerodynamic shaping used"],
        ["Ship hulls","Froude number governs wave-making resistance; Reynolds governs skin friction\nBulbous bow reduces bow wave; hull polish maintains laminar regions"],
        ["Wind turbines","Aerofoil blades extract kinetic energy from wind\nTip speed ratio λ = Rω/U∞ optimised; Betz limit: max efficiency = 16/27 ≈ 59.3%"],
        ["Sports balls","Seam, spin, and surface roughness exploit transition and Magnus effects\nDimples (golf), seam angle (cricket), fuzz (tennis) all engineered to alter C_D and C_L"],
        ["Submarine / AUV","Operate at high Re in water (ν much lower than air)\nStreamlined axisymmetric bodies; control surfaces act as aerofoils for manoeuvring"],
        ["Biomedical microflow","Red blood cells in capillaries: Re ≈ 0.001 — pure Stokes flow\nDrag purely viscous; no inertial effects — Stokes' law applies directly"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Reynolds number:  Re = ρUL/μ = UL/ν",
        "Drag force:  F_D = ½ρU²A · C_D  (A = reference area)",
        "Lift force:  F_L = ½ρU²S · C_L  (S = wing planform area)",
        "Kutta–Joukowski lift:  L = ρU∞Γ  (Γ = circulation)",
        "Stokes drag (Re ≪ 1):  F_D = 3πμDU",
        "Boundary layer thickness (laminar):  δ = 5x / √Re_x",
        "Skin friction (laminar flat plate):  C_f = 1.328 / √Re_L",
        "Strouhal number (vortex shedding):  St = fD/U∞  ≈ 0.2",
        "Bernoulli (inviscid, steady):  p + ½ρv² + ρgh = constant",
        "Induced drag:  C_Di = C_L² / (π · AR · e)  (e = Oswald efficiency factor)",
      ]}/>
    </div>
  );
}